import React, { useCallback, useEffect, useRef } from 'react';
import useListState from 'hooks/useListState';
import {
  Box,
  Flex,
  Text,
  Button,
  Icon,
  Badge,
  useDisclosure,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import DeleteModal from 'components/popUpModel/DeleteModal';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import CouponListFilter from './CouponListFilter';
import {
  ListCouponData,
  DeleteCouponData,
} from '../../../reactRedux/thunks/couponThunk/couponThunk';
import { PiPencil, PiTrash } from 'react-icons/pi';
import moment from 'moment';
import { sideBarRoutes } from 'utils/StaticData';
import { getCouponTypeDisplay } from './CouponDetail';

const CouponList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tabletextColor } = useColorModeVariables();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const couponDeleteRef = useRef(null);

  const { params, setParam, setParams, debouncedParams, page, setPage } =
    useListState({
      defaults: { search: '', status: '', couponType: '', limit: 10 },
      debounceKeys: ['search'],
    });

  const { isLoading, couponListData } = useSelector(
    (state) => state.CouponSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);

  // Check if user has permission to manage coupons (only Admin and Dealer)
  const canManageCoupons =
    adminDetails?.role === sideBarRoutes?.Admin ||
    adminDetails?.role === sideBarRoutes?.Dealer;

  const getCouponList = useCallback(() => {
    dispatch(
      ListCouponData({
        page: page,
        limit: params.limit || 10,
        search: debouncedParams.search,
        isActive: params.status,
        couponType: params.couponType,
      }),
    );
  }, [
    dispatch,
    page,
    params.limit,
    debouncedParams.search,
    params.status,
    params.couponType,
  ]);

  useEffect(() => {
    getCouponList();
  }, [getCouponList]);

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { color: 'green', label: 'Active' },
      inactive: { color: 'gray', label: 'Inactive' },
      expired: { color: 'red', label: 'Expired' },
      pending: { color: 'yellow', label: 'Pending' },
    };

    const statusConfig = statusMap[status === true ? 'active' : 'inactive'] || {
      color: 'gray',
      label: status,
    };

    return (
      <Badge colorScheme={statusConfig.color} variant="solid" fontSize="xs">
        {statusConfig.label}
      </Badge>
    );
  };

  const formatDateForEdit = (dateString) => {
    if (!dateString) return '';

    return moment.parseZone(dateString).format('DD-MM-YYYY');
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.couponCode, {
      id: 'couponCode',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Coupon Code
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.couponType, {
      id: 'couponType',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Type
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {getCouponTypeDisplay(info.getValue()) || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.isActive, {
      id: 'isActive',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Status
        </Text>
      ),
      cell: (info) => getStatusBadge(info.getValue()),
    }),

    columnHelper.accessor((row) => row?.startDate, {
      id: 'startDate',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Valid From
        </Text>
      ),
      cell: (info) => {
        const dateValue = info.getValue();
        return (
          <Text
            color={tabletextColor}
            fontSize="sm"
            align="left"
            whiteSpace="nowrap"
          >
            {dateValue ? formatDateForEdit(dateValue) : 'N/A'}
          </Text>
        );
      },
    }),

    columnHelper.accessor((row) => row?.endDate, {
      id: 'endDate',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Valid Until
        </Text>
      ),
      cell: (info) => {
        const dateValue = info.getValue();
        return (
          <Text
            color={tabletextColor}
            fontSize="sm"
            align="left"
            whiteSpace="nowrap"
          >
            {dateValue ? formatDateForEdit(dateValue) : 'N/A'}
          </Text>
        );
      },
    }),

    columnHelper.accessor((row) => row?.usageCount, {
      id: 'usage',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Usage
        </Text>
      ),
      cell: (info) => {
        const coupon = info.row.original;
        const usageText = coupon.maxUsers
          ? `${coupon.usageCount || 0}/${coupon.maxUsers}`
          : `${coupon.usageCount || 0}`;

        return (
          <Text
            color={tabletextColor}
            fontSize="sm"
            align="left"
            whiteSpace="nowrap"
          >
            {usageText}
          </Text>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'actions',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Actions
        </Text>
      ),
      cell: (info) => (
        <HStack spacing={2} className="no-navigate">
          {canManageCoupons && (
            <>
              <Tooltip label="Edit Coupon">
                <Link
                  to={`/admin/coupons/edit/${info?.row?.original?.couponId}`}
                  className="btn btn-light rounded-3 p-1"
                >
                  <PiPencil
                    size={20}
                    title="Edit"
                    style={{ marginRight: '20px' }}
                  />
                </Link>
              </Tooltip>
              <Tooltip label="Delete Coupon">
                <div className="btn btn-danger rounded-3 p-1">
                  <PiTrash
                    size={20}
                    className="text-white"
                    onClick={() => {
                      couponDeleteRef.current = info.getValue()?.couponId;
                      onOpen();
                    }}
                    title="Delete"
                  />
                </div>
              </Tooltip>
            </>
          )}
        </HStack>
      ),
    }),
  ];

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      {/* Filters */}
      <CouponListFilter
        search={params.search}
        setSearch={(val) => setParam('search', val)}
        status={params.status}
        setStatus={(val) => setParam('status', val)}
        setPage={setPage}
        type={params.couponType}
        setType={(val) => setParam('couponType', val)}
      />

      {/* Add Button */}
      {canManageCoupons && (
        <Flex justify="flex-end" mb={4}>
          <Button
            bg="reviveOrange"
            color="primary"
            borderRadius="12px"
            _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
            onClick={() => navigate('/admin/coupons/add')}
          >
            <Icon
              as={FaPlus}
              width="15px"
              height="15px"
              marginRight="5px"
              color="inherit"
            />
            Add Coupon
          </Button>
        </Flex>
      )}

      {/* Table */}
      <CommonTable
        columns={columns}
        data={couponListData?.list || []}
        isLoading={isLoading}
        keyValue="couponId"
        rowclick={['actions']}
        isLink={true}
        parentLink="/admin/coupons/details"
        dynamicWidth={{
          code: '150px',
          name: '200px',
          discount: '150px',
          status: '120px',
          validFrom: '130px',
          validUntil: '130px',
          usage: '100px',
          actions: '150px',
        }}
      />

      {/* Pagination */}
      {couponListData?.total > 10 && (
        <CommonPagination
          totalCount={couponListData?.total}
          activePage={page}
          pageCount={Math.ceil(
            couponListData?.total / (couponListData?.limit || 10),
          )}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isOpen}
        onClose={onClose}
        title="Are you sure you want to delete this coupon?"
        SolidBTNText="Delete"
        onDelete={() => {
          dispatch(
            DeleteCouponData(couponDeleteRef.current, {}, () => {
              if (page > 1 && couponListData?.list?.length === 1) {
                setPage(page - 1);
              } else {
                getCouponList();
              }
              onClose();
            }),
          );
        }}
        loader={isLoading}
      />
    </Box>
  );
};

export default CouponList;
